import React, { useRef, useState } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { UiRichTextEditor } from '../../ui/ui-rich-text-editor';

import { LegacyMessageTemplatePreview } from './legacy-message-template-preview';
import { MessageTemplatePreview } from './message-template-preview';

import { ConfirmableMessageText } from '../../automations/utils/applicant-automation-display-utils';
import { plainTextEditorConfig, richTextEditorConfig } from '../../util/ckeditor-config';
import { CkEditorNewlineTrimmer } from '../../util/ckeditor-newline-trimmer';
import { MESSAGE_TYPE } from '../../util/message-types';
import { PersonalizeButton } from '../../widgets/message-composer/toolbar/personalize-button';

type InputProps<T> = {
  parser: MessageTemplateParser;

  communicationTemplate?: null | T;
  caption?: string;

  isAutomated?: boolean;
  useRichText?: boolean;
  stripHtmlFromMessage?: boolean;
  showBulkUpdate?: boolean;
  showUpdateDefault?: boolean;
  orgName?: string;
  recipientType: ContactType;
  templateType: TemplateType;

  onSave: (communicationTemplate: T & ApplicantAutomationUpdate, options?: any) => void;
  onClose?: () => void;
};

function hasIsConfirmationRequired(
  template?: null | Template,
): template is TextMessageTemplateRequiresConfirmation {
  return (
    Boolean(template) &&
    (template as TextMessageTemplateRequiresConfirmation).isConfirmationRequired !== undefined
  );
}

function hasIsConfirmable(template?: null | Template): template is ApplicantAutomation {
  return Boolean(template) && (template as ApplicantAutomation).isConfirmable !== undefined;
}

const isCommentTemplate = (template?: null | Template): template is CommentTemplate =>
  Boolean(template) && (template as CommentTemplate).jobId !== undefined;

export const EditMessageTemplateDialog = <T extends Template>(props: InputProps<T>) => {
  const {
    communicationTemplate,
    caption,
    parser,
    isAutomated,
    useRichText,
    stripHtmlFromMessage,
    showBulkUpdate,
    showUpdateDefault,
    orgName,
    recipientType,
    templateType,
    onSave,
    onClose,
  } = props;

  const [templateString, setTemplateString] = useState<string>(
    communicationTemplate?.template || '',
  );
  const [isConfirmationRequired, setIsConfirmationRequired] = useState<boolean>(
    hasIsConfirmationRequired(communicationTemplate)
      ? Boolean(communicationTemplate.isConfirmationRequired)
      : false,
  );
  const [preventSaving, setPreventSaving] = useState<boolean>(false);
  const [willUpdateAllJobs, setWillUpdateAllJobs] = useState<boolean>(false);
  const [willUpdateDefault, setWillUpdateDefault] = useState<boolean>(false);

  const [useApplicantTemplateForAllJobs, setUseApplicantTemplateForAllJobs] = useState<boolean>(
    isCommentTemplate(communicationTemplate) ? !communicationTemplate?.jobId : false,
  );

  const editorRef = useRef<any>();

  const changeRichTextMessage = (e: {
    editor: { getData: Function; editable: Function; focus: Function };
  }) => {
    setTemplateString(e.editor.getData());

    // IF (07/15/21): This focus is to handle the case when users insert template variables from the
    // toolbar selector. Doing so pulls focus to that select, and we want the user to be able to
    // continue typing without having to click back into the box.
    e.editor.focus();
  };

  const handleIsConfirmationRequiredChange = () => {
    setIsConfirmationRequired(!isConfirmationRequired);
  };

  const handleUseTemplateForAllJobsChange = () => {
    setUseApplicantTemplateForAllJobs(!useApplicantTemplateForAllJobs);
  };

  const submit = async () => {
    let cleanTemplate = templateString;
    if (stripHtmlFromMessage) {
      cleanTemplate = CkEditorNewlineTrimmer.trimMessage(templateString);
    }

    const bulkUpdateOptions: { willUpdateAllJobs?: boolean; willUpdateDefault?: boolean } = {};
    if (showBulkUpdate) {
      bulkUpdateOptions.willUpdateAllJobs = willUpdateAllJobs;
    }
    if (showUpdateDefault) {
      bulkUpdateOptions.willUpdateDefault = willUpdateDefault;
    }

    const newTemplate = {
      ...props.communicationTemplate,
      template: cleanTemplate,
      isConfirmationRequired,
      ...bulkUpdateOptions,
    } as T & ApplicantAutomationUpdate;

    const options = { useApplicantTemplateForAllJobs };

    await onSave(newTemplate, options);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleVariableSelect = (variable: string) => {
    const { editor } = editorRef.current;
    editor.insertText(`${variable} `);
  };

  const onError = () => {
    setPreventSaving(true);
  };

  const onChange = () => {
    setPreventSaving(false);
  };

  const handleWillUpdateAllJobsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWillUpdateAllJobs(event.target.checked);
  };

  const handleWillUpdateDefaultChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWillUpdateDefault(event.target.checked);
  };

  const messageForPreview = {
    template: templateString,
    isConfirmationRequired,
  };
  const buttonPosition = useRichText ? { top: 10, left: 270 } : { top: 10, left: 50 };

  const title = templateType === 'comment' ? 'Saved Note' : 'Saved Message';

  return (
    <UiDialog
      title={`${communicationTemplate ? 'Edit' : 'Add'} ${title}`}
      onClose={handleClose}
      size="sm"
    >
      <UiDialog.Content>
        {caption ? <div className="typo-subtitle">{caption}</div> : null}
        <div className="u-position-relative">
          <PersonalizeButton
            style={{ position: 'absolute', ...buttonPosition }}
            onVariableSelect={handleVariableSelect}
            messageType={useRichText ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
            recipientType={recipientType}
          />
          <UiRichTextEditor
            data={templateString || ''}
            ref={editorRef}
            onChange={changeRichTextMessage}
            config={useRichText ? richTextEditorConfig : plainTextEditorConfig}
            injectCKEditorLineEndings={stripHtmlFromMessage}
          />
        </div>
        {templateType === 'comment' && recipientType === 'applicant' && (
          <div className="u-display-flex u-margin-bottom u-alignItems-center">
            <Checkbox
              checked={useApplicantTemplateForAllJobs || false}
              onChange={handleUseTemplateForAllJobsChange}
            />
            <div>Use note template for all jobs</div>
          </div>
        )}
        {hasIsConfirmable(communicationTemplate) && communicationTemplate.isConfirmable ? (
          <div className="u-display-flex u-margin-y">
            <Checkbox
              checked={isConfirmationRequired || false}
              onChange={handleIsConfirmationRequiredChange}
            />
            <div>
              Ask for confirmation (this will add a prompt to the end of your message: &quot;
              {ConfirmableMessageText}&quot;)
            </div>
          </div>
        ) : null}
        <div className="typo-h6 u-margin-top-lg">Preview:</div>
        <FeatureFlagged
          variant={FeatureGatedVariant.HIDE_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <LegacyMessageTemplatePreview
            message={messageForPreview}
            parser={parser}
            isAutomated={isAutomated || false}
            onError={onError}
            onChange={onChange}
            messageType={useRichText ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
            templateType={templateType}
          />
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <MessageTemplatePreview
            message={messageForPreview}
            parser={parser}
            onError={onError}
            onChange={onChange}
            messageType={useRichText ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
            templateType={templateType}
          />
        </FeatureFlagged>
        {showBulkUpdate && (
          <FormControlLabel
            control={
              <Checkbox checked={willUpdateAllJobs} onChange={handleWillUpdateAllJobsChange} />
            }
            label={`Update this message for all ${orgName ? `${orgName} ` : ''}jobs`}
          />
        )}
        {showUpdateDefault && (
          <FormControlLabel
            control={
              <Checkbox checked={willUpdateDefault} onChange={handleWillUpdateDefaultChange} />
            }
            label={`Update the default for this message for future ${
              orgName ? `${orgName} ` : ''
            }jobs`}
          />
        )}
        <div className="u-textAlign-right u-margin-top-lg">
          <UiButton variant="dismiss" onClick={handleClose} className="u-margin-right">
            Cancel
          </UiButton>
          <UiButton disabled={preventSaving} onClick={submit}>
            Save Message
          </UiButton>
        </div>
      </UiDialog.Content>
    </UiDialog>
  );
};
